import { createContext, useContext, useId, type ComponentType, type ReactNode } from "react";
import { useInRouterContext, useLocation, useNavigate } from "react-router-dom";

import { cn } from "../../lib/utils";
import { Button } from "./button";
import { ArrowLeft } from "lucide-react";

type BackButtonProps = {
  /**
   * Where "back" goes when there is no in-app history to return to — a deep link, a refresh, a new tab, or a
   * link someone pasted into chat. It is the FALLBACK, not the primary behaviour.
   */
  onClick: () => void;
  label?: string;
  /**
   * Set false when "back" is not a navigation at all — a wizard stepping to its previous step, a sheet
   * clearing its own state. Browser history knows nothing about those, and sending it backwards would leave
   * the page instead of stepping within it.
   */
  historyFirst?: boolean;
  /**
   * Render as an icon-only chevron instead of a full labelled button — for a compact header bar where the
   * label would cost a row. The label is kept as the accessible name (`aria-label` + tooltip), so it stays
   * discoverable without taking space.
   */
  compact?: boolean;
};

function BackButtonBase({
  onClick,
  label,
  compact,
}: {
  onClick: () => void;
  label: string;
  compact?: boolean;
}) {
  if (compact) {
    return (
      <Button
        type='button'
        variant='ghost'
        size='icon'
        onClick={onClick}
        aria-label={label}
        title={label}
        className='size-8 shrink-0'
      >
        <ArrowLeft className='size-4' aria-hidden='true' />
      </Button>
    );
  }

  // GHOST, not primary. "Back" is never the thing a page wants you to do — it is the way out. Rendered as a
  // filled primary button it outweighed the real primary action beside it (Save, Create, Approve) and read as
  // the page's main call to action. This is the labelled sibling of the compact arrow `DetailPageHeader` uses,
  // so the two forms of "back" now look like the same affordance at different sizes.
  return (
    <Button
      type='button'
      variant='ghost'
      size='sm'
      onClick={onClick}
      className='-ml-2 w-fit gap-1.5 text-muted-foreground hover:text-foreground'
    >
      <ArrowLeft className='size-4' aria-hidden='true' />
      <span>{label}</span>
    </Button>
  );
}

/**
 * "Back" means "where I just came from" — and a hard-coded destination cannot know that.
 *
 * Every caller passed a FIXED route ("back to reports", "back to the budgets list"), so a user who reached the
 * page from somewhere else — a drill-down from another report, a dashboard widget, a search result — was sent
 * somewhere they had never been. It looked like a navigation bug because it was one.
 *
 * So: go back through history when there IS history, and fall back to the caller's route when there is not.
 *
 * `location.key` is how we tell the difference. React Router stamps a fresh key on every in-app navigation and
 * leaves the session's first entry as "default" — so `key === "default"` means the user LANDED here (deep link,
 * refresh, pasted URL) and going back would walk them out of the app entirely. That is the case the fallback
 * exists for.
 */
function HistoryAwareBackButton({
  onClick,
  label,
  compact,
}: {
  onClick: () => void;
  label: string;
  compact?: boolean;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const hasAppHistory = location.key !== "default";

  return (
    <BackButtonBase
      label={label}
      compact={compact}
      onClick={() => (hasAppHistory ? navigate(-1) : onClick())}
    />
  );
}

export function BackButton({ onClick, label = "Back", historyFirst = true, compact }: BackButtonProps) {
  // Rendered outside a Router (tests, storybook), the history hooks would throw — so we check first and render
  // the plain button. A conditional COMPONENT is fine here; a conditional hook would not be.
  const inRouter = useInRouterContext();

  if (!historyFirst || !inRouter) {
    return <BackButtonBase label={label} onClick={onClick} compact={compact} />;
  }

  return <HistoryAwareBackButton label={label} onClick={onClick} compact={compact} />;
}

/**
 * Where an app-supplied addon sits inside a page header.
 *
 * `AfterTitle` is inline, right after the heading (a small pill). `Aside` is on the header's right side, before
 * the page's own actions (a compact card).
 */
export enum PageHeaderAddonPosition {
  AfterTitle = "AFTER_TITLE",
  Aside = "ASIDE",
}

export type PageHeaderAddonProps = {
  position: PageHeaderAddonPosition;
  /** Stable per header instance, so an addon rendered in both positions knows they belong to one header. */
  headerId: string;
};

/**
 * Lets an app put something into every page header without editing each page, and without this package
 * importing app code: the app provides a component, the header renders it in both positions. Unset (the
 * default) means headers render exactly as before.
 */
const PageHeaderAddonContext = createContext<ComponentType<PageHeaderAddonProps> | null>(null);

export function PageHeaderAddonProvider({
  addon,
  children,
}: {
  addon: ComponentType<PageHeaderAddonProps> | null;
  children: ReactNode;
}) {
  return <PageHeaderAddonContext.Provider value={addon}>{children}</PageHeaderAddonContext.Provider>;
}

function usePageHeaderAddon() {
  const Addon = useContext(PageHeaderAddonContext);
  const headerId = useId();
  return { Addon, headerId };
}

type PageHeaderProps = {
  title: string;
  description?: string;
  /** Optional leading element shown next to the title (e.g. a logo/avatar). */
  icon?: ReactNode;
  subnav?: ReactNode;
  actions?: ReactNode;
  className?: string;
  /** Fallback destination for "back" when there is no in-app history (deep link / refresh). */
  onBack?: () => void;
  /** False when "back" is not a navigation (a wizard step, a sheet closing) — see {@link BackButton}. */
  backHistoryFirst?: boolean;
  backLabel?: string;
  backAction?: ReactNode;
};

export function PageHeader({
  title,
  description,
  icon,
  subnav,
  actions,
  className,
  onBack,
  backHistoryFirst,
  backLabel = "Back",
  backAction,
}: PageHeaderProps) {
  const resolvedBackAction =
    backAction ??
    (onBack ? <BackButton onClick={onBack} label={backLabel} historyFirst={backHistoryFirst} /> : null);
  const { Addon, headerId } = usePageHeaderAddon();
  const heading = <h1 className='text-3xl font-semibold tracking-tight'>{title}</h1>;

  return (
    <div className={cn("space-y-3", className)}>
      <div className='flex flex-wrap items-start justify-between gap-3'>
        <div className='min-w-0 space-y-1'>
          {resolvedBackAction}
          <div className='flex items-center gap-3'>
            {icon ? <div className='shrink-0'>{icon}</div> : null}
            <div className='min-w-0'>
              {Addon ? (
                <div className='flex flex-wrap items-center gap-x-3 gap-y-1'>
                  {heading}
                  <Addon position={PageHeaderAddonPosition.AfterTitle} headerId={headerId} />
                </div>
              ) : (
                heading
              )}
              {description ? <p className='text-sm text-muted-foreground'>{description}</p> : null}
            </div>
          </div>
        </div>
        {Addon ? (
          <div className='flex flex-wrap items-center justify-end gap-3 empty:hidden'>
            <Addon position={PageHeaderAddonPosition.Aside} headerId={headerId} />
            {actions ? <div className='flex items-center gap-2'>{actions}</div> : null}
          </div>
        ) : actions ? (
          <div className='flex items-center gap-2'>{actions}</div>
        ) : null}
      </div>
      {subnav ? <div className='space-y-3'>{subnav}</div> : null}
    </div>
  );
}

type DetailPageHeaderProps = {
  /**
   * The primary heading — the specific record you drilled INTO (an account, a report, a member), not the
   * module you drilled into it FROM. A string is the common case; a node is allowed for a title that carries
   * its own inline element.
   */
  title: ReactNode;
  /**
   * Inline meta shown right after the title on the same row: a status, a context, a balance. Pass badges or
   * plain text. It is what turns "where am I" into "where am I, and in what state" without costing a row.
   */
  meta?: ReactNode;
  /**
   * A single short line of context under the title, for detail pages that genuinely have something to say
   * about the record ("Every entry for an account with a running balance"). Keep it to one line — this is the
   * COMPACT header; a paragraph belongs on the module's landing `PageHeader`, not here.
   */
  description?: ReactNode;
  /** Optional leading element before the title (an avatar, an account-type icon). */
  icon?: ReactNode;
  /**
   * Right-aligned actions — fully custom render. Buttons, a menu, a Help link; whatever the page needs. The
   * header takes no opinion on what goes here beyond placing and spacing it.
   */
  actions?: ReactNode;
  /**
   * Sub-navigation shown on its own row beneath the bar (route tabs, a segmented control). Kept a slot rather
   * than a typed `tabs` prop so any tab implementation fits.
   */
  tabs?: ReactNode;
  className?: string;
  /** Back fallback destination, for when there is no in-app history — see {@link BackButton}. Omit for no back. */
  onBack?: () => void;
  backLabel?: string;
  /** False when "back" is not a navigation (a wizard step, a sheet closing) — see {@link BackButton}. */
  backHistoryFirst?: boolean;
};

/**
 * A COMPACT header for a detail page — the record you drilled into, on a single bar.
 *
 * `PageHeader` is the module's landing header: a big title, a paragraph of description, a context line. That is
 * right for a list page and wrong for a detail page, where it stacks a second header on top of the entity's own
 * and repeats a description that no longer applies ("Configure income & expense categories…" above one bank
 * account). The result was six rows of chrome before any content.
 *
 * This is the detail-page answer: one row — `‹ Title · meta … actions` — with optional tabs beneath. It is a
 * shared primitive on purpose; every detail page across the app should reach for it rather than re-assemble a
 * back button + heading + tabs by hand, which is how they drifted apart in the first place.
 */
export function DetailPageHeader({
  title,
  meta,
  description,
  icon,
  actions,
  tabs,
  className,
  onBack,
  backLabel = "Back",
  backHistoryFirst,
}: DetailPageHeaderProps) {
  const { Addon, headerId } = usePageHeaderAddon();

  return (
    <div className={cn("space-y-3", className)}>
      <div className='flex flex-wrap items-start justify-between gap-3'>
        <div className='min-w-0 flex-1 space-y-1'>
          {/* Back, icon, title and meta share ONE line — the compact bar. The optional description sits just
              beneath it, so a page keeps its context without the back button ever taking a row of its own. */}
          <div className='flex flex-wrap items-center gap-x-3 gap-y-1'>
            {onBack ? (
              <BackButton compact onClick={onBack} label={backLabel} historyFirst={backHistoryFirst} />
            ) : null}
            {icon ? <span className='shrink-0'>{icon}</span> : null}
            <h1 className='min-w-0 truncate text-xl font-semibold tracking-tight'>{title}</h1>
            {meta ? (
              <div className='flex min-w-0 flex-wrap items-center gap-1.5 text-sm text-muted-foreground'>
                {meta}
              </div>
            ) : null}
            {Addon ? <Addon position={PageHeaderAddonPosition.AfterTitle} headerId={headerId} /> : null}
          </div>
          {description ? <p className='text-sm text-muted-foreground'>{description}</p> : null}
        </div>
        {Addon ? (
          <div className='flex shrink-0 flex-wrap items-center justify-end gap-3 empty:hidden'>
            <Addon position={PageHeaderAddonPosition.Aside} headerId={headerId} />
            {actions ? <div className='flex shrink-0 items-center gap-2'>{actions}</div> : null}
          </div>
        ) : actions ? (
          <div className='flex shrink-0 items-center gap-2'>{actions}</div>
        ) : null}
      </div>
      {tabs ? <div>{tabs}</div> : null}
    </div>
  );
}
